/*
 * Maison Avéla — PDP hero gallery swipe hint.
 *
 * This is a SEPARATE system from assets/maison-buy-pill.js (the "Може да ти
 * хареса" / homepage product carousel). It touches only the main product
 * media gallery on the product page — one <slideshow-component> inside
 * .product-information__media — and nothing else.
 *
 * Everything else about the gallery (drag, touch swipe, active-slide
 * tracking, thumbnail sync, click-to-zoom) is Horizon's own
 * <slideshow-component> (assets/slideshow.js) — completely untouched and
 * reused as-is. This file only adds a small "SWIPE" hint that appears once
 * and fades out as soon as the visitor starts interacting with the gallery,
 * the same interaction concept already used on the homepage carousel.
 */
(() => {
  const ROOT = "main[data-template='product'] .product-information__media";
  const READY = 'maPdpGalleryReady';

  /**
   * Mounts the hint inside the slideshow-container (a positioned ancestor
   * shared with the floating thumbnail navigation), only when there is more
   * than one image/media item to swipe between.
   */
  const mountHint = (root) => {
    const scroller = root.querySelector('slideshow-slides');
    const container = root.querySelector('slideshow-container');
    if (!scroller || !container) return false;
    if (scroller.scrollWidth <= scroller.clientWidth + 4) return false;
    if (container.querySelector('.ma-swipe')) return true;

    const hint = document.createElement('span');
    hint.className = 'ma-swipe ma-swipe--pdp';
    hint.setAttribute('aria-hidden', 'true');
    hint.textContent = 'SWIPE';
    container.appendChild(hint);

    let travelled = 0;
    let last = scroller.scrollLeft;
    let retired = false;

    const retire = () => {
      if (retired) return;
      retired = true;
      hint.classList.add('ma-swipe--gone');
      scroller.removeEventListener('scroll', onScroll);
      root.removeEventListener('pointerdown', onPointerDown);
      window.setTimeout(() => hint.remove(), 600);
    };

    const onScroll = () => {
      travelled += Math.abs(scroller.scrollLeft - last);
      last = scroller.scrollLeft;
      // A nudge is not an interaction. Roughly a third of a card.
      if (travelled > 40) retire();
    };

    // Dismiss immediately on the first press/tap, even before any scroll
    // delta has registered — dragging, clicking a thumbnail, or tapping the
    // image to zoom should all retire the hint right away.
    const onPointerDown = () => retire();

    scroller.addEventListener('scroll', onScroll, { passive: true });
    root.addEventListener('pointerdown', onPointerDown, { passive: true });

    return true;
  };

  const init = (root) => {
    if (!root || root.dataset[READY] === 'true') return;
    root.dataset[READY] = 'true';

    let tries = 0;
    const tryMount = () => {
      if (mountHint(root) || ++tries > 15) return true;
      window.setTimeout(tryMount, 200);
      return false;
    };

    if (!('IntersectionObserver' in window)) {
      requestAnimationFrame(tryMount);
      return;
    }

    const seen = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        seen.disconnect();
        tryMount();
      },
      { rootMargin: '200px' }
    );
    seen.observe(root);
  };

  const boot = (scope) => {
    (scope || document).querySelectorAll(ROOT).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // The theme editor replaces the section wholesale; the replacement is a
  // fresh element without the ready flag, so this cannot double-bind.
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!section || !section.querySelector) return;
    boot(section);
  });
})();
